import type { TemplateAssignmentScope } from '@Application/Documents/TemplateAssignments/TemplateAssignmentScope';
import { theme } from '@Guidelines/Theme/theme';
import TemplateAssignmentCardActions from '@UI/Pages/DocumentTemplates/Shared/TemplateAssignmentCard/TemplateAssignmentCardActions';
import type DocumentTemplatesListView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplatesListView';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type TemplateAssignmentView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/TemplateAssignmentView';
import { css } from '@emotion/react';
import * as React from 'react';
import { GridItem } from '@verclam/grid-system';

const cardStyle = (isValid: boolean) => css`
	padding: 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${!isValid ? theme.states.warning.ink : theme.colors.border};
	opacity: 1;
`;

const typeNameStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const titleRowStyle = css`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.75rem;
`;

const statusStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly assignment: TemplateAssignmentView;
	readonly scope: TemplateAssignmentScope;
	/** Every gabarit of the organization: the actions row filters it to the entry's own document type. */
	readonly templates: DocumentTemplatesListView;
	/** Resolves the entry's document type label — the type itself is only an identifier on the wire. */
	readonly documentTypes: DocumentTypesListView;
	/**
	 * Extra content appended after the actions row. The organization screen uses it for the list of
	 * facility overrides of this document type — organization-only information the facility screen has
	 * no use for and never passes.
	 */
	readonly children?: React.ReactNode;
	/**
	 * Extra actions rendered next to the type's own title, in the same track — the organization
	 * screen's rename/archive actions for a free type (`DocumentTypeActions`). The facility screen
	 * never passes this: it never manages types at all.
	 */
	readonly typeActions?: React.ReactNode;
};

/**
 * One document type, shared by the organization and the facility affectation screens: one response
 * shape, one card. `scope.type` alone decides the status line: at the organization level it is always
 * the type's own assignment (`inheritedTemplate` is always `null` there); at the facility level it is
 * the facility's own override, else the organization's inherited gabarit, else none at all. The
 * dropdown and the remove action live in `TemplateAssignmentCardActions`, which reads the same `scope`
 * to phrase itself for either level.
 *
 * Emits exactly `CARD_TRACK_COUNT` direct children, on every branch, so sibling cards sharing a visual
 * row settle block for block. Its parent grid must hold it as a DIRECT child: a wrapper that does not
 * relay the row axis leaves the subgrid inert, and nothing reports it. `typeActions` shares the title
 * track with the `h3` rather than adding a track of its own — the track count stays the same whether
 * or not a caller passes it.
 */
const TemplateAssignmentCard: React.FC<Props> = ({
	assignment,
	scope,
	templates,
	documentTypes,
	children,
	typeActions,
}) => {
	const ownTemplate = React.useMemo(
		() => templates.findById(assignment.templateId),
		[templates, assignment.templateId],
	);

	const inheritedTemplate = React.useMemo(
		() => templates.findById(assignment.inheritedTemplateId),
		[templates, assignment.inheritedTemplateId],
	);

	// One line, always: the status track owes a box on every branch, so the four cases resolve to a
	// single string rather than to four siblings of which three render nothing.
	const statusText = React.useMemo(() => {
		if (scope.type === 'organization') {
			return ownTemplate !== null ? ownTemplate.name : 'Aucun gabarit';
		}

		if (assignment.isOverride) {
			// An override naming a gabarit absent from the catalogue is a data anomaly, not a state the
			// screen can phrase: it degrades to the same line as no gabarit at all.
			return ownTemplate !== null ? `Surcharge de l’agence : ${ownTemplate.name}` : 'Aucun gabarit';
		}

		return inheritedTemplate !== null
			? `Gabarit de la structure : ${inheritedTemplate.name}`
			: 'Aucun gabarit — le téléchargement de ce document sera impossible.';
	}, [scope.type, assignment.isOverride, ownTemplate, inheritedTemplate]);

	return (
		<GridItem
			subgrid={'rows'}
			columnSize={{ xl: 1, lg: 2, md: 2 }}
			css={cardStyle(ownTemplate !== null || inheritedTemplate !== null)}
		>
			<div css={titleRowStyle}>
				<h3 css={typeNameStyle}>{documentTypes.labelOf(assignment.documentTypeId)}</h3>
				{typeActions}
			</div>

			<p css={statusStyle}>{statusText}</p>

			<TemplateAssignmentCardActions
				assignment={assignment}
				scope={scope}
				templates={templates}
				documentTypes={documentTypes}
			/>

			{children ?? <div />}
		</GridItem>
	);
};

export default TemplateAssignmentCard;
